import { EVAL_WINDOWS, firstEvalValue, useEvaluationMetric } from '@/features/forecasts/hooks/useForecastEvaluation';
import type { EvalModel, EvalWindowKey } from '@/features/forecasts/types';

type Props = { model: EvalModel };

const fmtPct = (v: number | null) => (v == null ? '—' : `${(v * 100).toFixed(1)}%`);

// Short labels for the inline callout (the table carries the long ones).
const WINDOWS: { key: EvalWindowKey; label: string }[] = [
  { key: 'week', label: '1W' },
  { key: 'month', label: '1M' },
  { key: 'quarter', label: '3M' },
];

function WapeStat({ model, window, label }: { model: EvalModel; window: EvalWindowKey; label: string }) {
  const { data, isLoading, isError } = useEvaluationMetric({ model, metric: 'wape', window });
  const value = firstEvalValue(data?.results);
  return (
    <div className="flex flex-col items-end" title={EVAL_WINDOWS[window].label}>
      <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{label} WAPE</span>
      <span className="text-sm font-semibold tabular-nums text-gray-900">
        {isLoading ? <span className="text-gray-300">…</span> : isError ? '—' : fmtPct(value)}
      </span>
    </div>
  );
}

// WAPE callout for a single model (Incumbent or BFG), shown in the chart header.
// Displays all three windows (1 week / 1 month / 3 month) side by side, each
// reusing the same evaluation query that backs Bartley's comparison table.
export function WapeCallout({ model }: Props) {
  return (
    <div className="inline-flex items-stretch divide-x divide-gray-200 rounded-md border border-gray-200 bg-gray-50">
      {WINDOWS.map(({ key, label }) => (
        <div key={key} className="px-3 py-1.5">
          <WapeStat model={model} window={key} label={label} />
        </div>
      ))}
    </div>
  );
}
